import React, { useMemo } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  CardActionArea,
  Chip
} from '@mui/material'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import PhoneIcon from '@mui/icons-material/Phone'
import { SUPPORT_TICKET_STATUS } from '../../utils/enums'
import moment from 'moment'
import { gql, useQuery } from '@apollo/client'
import { getTicketMessages } from '../../apollo/queries'

const GET_TICKET_MESSAGES = gql`
  ${getTicketMessages}
`

const TicketCard = ({ ticket, isSelected, onClick }) => {
  const theme = useTheme()
  const { data: messageData } = useQuery(GET_TICKET_MESSAGES, {
    variables: { input: { ticket: ticket?._id, page: 1, limit: 100 } },
    skip: !ticket?._id,
    fetchPolicy: 'cache-first'
  })

  const getStatusLabel = status => {
    switch (status) {
      case SUPPORT_TICKET_STATUS.OPEN:
        return 'Open'
      case SUPPORT_TICKET_STATUS.IN_PROGRESS:
        return 'In Progress'
      case SUPPORT_TICKET_STATUS.CLOSED:
        return 'Closed'
      default:
        return status
    }
  }

  const getStatusColor = status => {
    switch (status) {
      case SUPPORT_TICKET_STATUS.OPEN:
        return theme.palette.info.main
      case SUPPORT_TICKET_STATUS.IN_PROGRESS:
        return theme.palette.warning.main
      case SUPPORT_TICKET_STATUS.CLOSED:
        return theme.palette.error.main
      default:
        return theme.palette.grey[500]
    }
  }

  const latestMessageText = useMemo(() => {
    const messages = messageData?.getTicketMessages?.messages || []
    if (!messages.length) return ticket.description

    const latestMessage = messages.reduce((latest, current) => {
      const latestTime =
        Number(latest?.createdAt) || new Date(latest?.createdAt).getTime() || 0
      const currentTime =
        Number(current?.createdAt) ||
        new Date(current?.createdAt).getTime() ||
        0
      return currentTime > latestTime ? current : latest
    }, messages[0])

    return latestMessage?.content?.trim() || ticket.description
  }, [messageData?.getTicketMessages?.messages, ticket.description])

  return (
    <Card
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: '14px',
        border: '1.5px solid',
        borderColor: isSelected ? 'primary.main' : 'rgba(16,24,40,0.08)',
        bgcolor: isSelected ? '#FFF6F0' : 'common.white',
        boxShadow: isSelected
          ? '0 10px 22px -12px rgba(244,102,31,0.6)'
          : '0 2px 8px -4px rgba(16,24,40,0.18)',
        transition: 'transform .15s ease, box-shadow .15s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isSelected
            ? '0 12px 24px -12px rgba(244,102,31,0.65)'
            : '0 10px 20px -12px rgba(16,24,40,0.3)'
        }
      }}>
      <CardActionArea onClick={onClick}>
        <CardContent sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              noWrap
              sx={{ maxWidth: '70%', color: theme.palette.common.black }}>
              {ticket.title}
            </Typography>
            <Chip
              label={getStatusLabel(ticket.status)}
              size="small"
              sx={{
                bgcolor: getStatusColor(ticket.status),
                color: '#fff',
                fontSize: '0.65rem',
                fontWeight: 'bold'
              }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: 1,
              color: theme.palette.text.secondary
            }}>
            {latestMessageText}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.disabled }}>
            {moment(
              isNaN(ticket.createdAt)
                ? ticket.createdAt
                : parseInt(ticket.createdAt)
            ).fromNow()}
          </Typography>
          {ticket?.orderId && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                color: theme.palette.text.secondary,
                fontWeight: 600
              }}>
              Order ID: {ticket.orderId}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              mt: 0.5
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <MailOutlineIcon
                sx={{ fontSize: '0.9rem', color: theme.palette.text.secondary }}
              />
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}>
                {ticket?.user?.email || 'No email'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <PhoneIcon
                sx={{ fontSize: '0.9rem', color: theme.palette.text.secondary }}
              />
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}>
                {ticket?.user?.phone || 'No phone'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export default TicketCard
